// Words people see for stored values. Keep these plain: they show in tables, menus and the Roles page.

import type { Permission, Role, VerificationStatus } from '@/api/types'

/** Where an org's business verification stands (Phase 10). */
export const VERIFICATION_LABEL: Record<VerificationStatus, string> = {
  unverified: 'Not verified',
  pending: 'Being checked',
  verified: 'Verified',
  rejected: 'Needs a fix',
}

export const ROLE_LABEL: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  builder: 'Builder',
  operator: 'Operator',
  viewer: 'Viewer',
}

export const ROLE_DESCRIPTION: Record<Role, string> = {
  owner: 'Everything, including billing, deleting the org and transferring ownership. One per org.',
  admin: 'Runs the org: settings, members, invites, flows, calls and developer keys.',
  builder: 'Builds and publishes flows, and exports results.',
  operator: 'Starts calls, listens in live and takes over or transfers calls.',
  viewer: 'Sees calls, flows and results. Changes nothing.',
}

/** Roles a member can be invited with or changed to; ownership moves only by transfer. */
export const ASSIGNABLE_ROLES: Role[] = ['admin', 'builder', 'operator', 'viewer']

/** Permissions grouped the way the Roles page shows them, with the phase that brings each area. */
export const PERMISSION_GROUPS: { title: string; note?: string; permissions: { key: Permission; label: string }[] }[] =
  [
    {
      title: 'Org',
      permissions: [
        { key: 'org.read', label: 'See the org' },
        { key: 'org.update', label: 'Change settings' },
        { key: 'org.delete', label: 'Delete the org' },
        { key: 'org.transfer_ownership', label: 'Transfer ownership' },
      ],
    },
    {
      title: 'Members',
      permissions: [
        { key: 'members.read', label: 'See members' },
        { key: 'members.invite', label: 'Invite people' },
        { key: 'members.update_role', label: 'Change roles' },
        { key: 'members.remove', label: 'Remove members' },
        { key: 'audit.read', label: 'Read the audit log' },
      ],
    },
    {
      title: 'Flows',
      permissions: [
        { key: 'flows.read', label: 'See flows' },
        { key: 'flows.edit', label: 'Edit flows' },
        { key: 'flows.publish', label: 'Publish flows' },
      ],
    },
    {
      title: 'Contacts',
      permissions: [
        { key: 'contacts.read', label: 'See contacts and their consent' },
        { key: 'contacts.manage', label: 'Add, import and edit contacts; record consent and opt-outs' },
      ],
    },
    {
      title: 'Calls',
      permissions: [
        { key: 'calls.read', label: 'See calls' },
        { key: 'calls.start', label: 'Start calls' },
        { key: 'calls.listen', label: 'Listen in live' },
        { key: 'calls.transfer', label: 'Take over and transfer' },
        { key: 'calls.test', label: 'Make test calls' },
        { key: 'numbers.manage', label: 'Manage phone and test numbers' },
        { key: 'voices.manage', label: 'Choose voices' },
      ],
    },
    {
      title: 'Results, developers and billing',
      note: 'Coming in later releases',
      permissions: [
        { key: 'results.read', label: 'See results' },
        { key: 'results.export', label: 'Export results' },
        { key: 'developers.manage', label: 'Manage API keys and webhooks' },
        { key: 'integrations.manage', label: 'Connect and manage stores (Shopify)' },
        { key: 'billing.manage', label: 'Manage billing and credits' },
      ],
    },
  ]

/** Audit log actions as sentences: "Asha changed a role". Unknown actions fall back to the raw value. */
export const AUDIT_ACTION_LABEL: Record<string, string> = {
  'user.signed_up': 'signed up',
  'user.email_verified': 'verified their email',
  'user.logged_in': 'logged in',
  'user.logged_out_everywhere': 'signed out everywhere',
  'user.password_changed': 'changed their password',
  'user.password_reset': 'reset their password',
  'user.profile_updated': 'updated their profile',
  'user.google_linked': 'linked Google sign-in',
  'session.revoked': 'ended a session',
  'org.created': 'created the org',
  'org.updated': 'changed org settings',
  'org.setup_saved': 'said what the org needs',
  'org.deleted': 'deleted the org',
  'org.ownership_transferred': 'transferred ownership',
  'org.suspended': 'suspended the org',
  'org.unsuspended': 'lifted the suspension',
  'member.role_changed': 'changed a role',
  'member.removed': 'removed a member',
  'member.left': 'left the org',
  'invite.created': 'invited someone',
  'invite.resent': 'resent an invite',
  'invite.revoked': 'revoked an invite',
  'invite.accepted': 'joined from an invite',
  'voices.updated': 'changed the voices',
  'voices.reset': 'reset the voices',
  'number.added': 'added a phone number',
  'number.updated': 'changed a phone number',
  'number.assigned': 'gave the org a phone number',
  'number.unassigned': 'took back a phone number',
  'test_number.added': 'added a test number',
  'test_number.removed': 'removed a test number',
  'flow.created': 'created a flow',
  'flow.published': 'published a flow',
  'flow.retired': 'retired a flow version',
  'flow.draft_discarded': 'discarded a flow draft',
  'flow.archived': 'archived a flow',
  'flow.restored': 'restored a flow',
  'contact.created': 'added a contact',
  'contact.updated': 'changed a contact',
  'contact.deleted': 'deleted a contact',
  'contacts.imported': 'imported contacts',
  'consent.recorded': 'recorded consent',
  'consent.withdrawn': 'withdrew consent',
  'opt_out.added': 'opted a number out',
  'call.listened': 'listened in on a call',
  'call.taken_over': 'took over a call',
  'results.exported': 'exported results',
  'api_key.created': 'created an API key',
  'api_key.rolled': 'rolled an API key',
  'api_key.revoked': 'revoked an API key',
  'app.connected': 'connected an app',
  'app.disconnected': 'disconnected an app',
  'billing.credit_added': 'added or adjusted credit',
  'billing.rate_changed': 'changed the per-minute rate',
  'billing.alert_changed': 'changed the low-balance alert',
  'billing.tds_credited': 'credited TDS',
  'org.verification_submitted': 'sent the business details for verification',
  'org.verified': 'verified the business',
  // Legacy: staff's per-org language switch, retired 2026-10-21; older entries still show.
  'org.languages_changed': 'changed the preview languages',
  'platform.updated': 'changed a platform',
  'platform.added': 'added a model',
  'notifications.updated': 'changed the org’s alerts',
  'org.platforms_set': 'set the org’s voice platform and model',
  'org.dlt_entity_set': 'changed the DLT entity ID',
  'slack.connected': 'connected Slack',
  'slack.disconnected': 'disconnected Slack',
  'store.connected': 'connected a store',
  'store.verified': 'checked a store’s connection',
  'store.setup_requested': 'started a store’s setup or resync',
  'store.disconnected': 'disconnected a store',
  'store.data_deleted': 'deleted a removed store’s data',
  'store.file_downloaded': 'downloaded a copy of what a store sent',
  'order.source_viewed': 'viewed what a store sent for an order',
  'order.refreshed': 'refreshed an order from its store',
  'org.verification_rejected': 'rejected the business details',
  'webhook.created': 'added a webhook endpoint',
  'webhook.updated': 'changed a webhook endpoint',
  'webhook.deleted': 'deleted a webhook endpoint',
  'webhook.secret_rolled': 'rolled a webhook secret',
  'sop.updated': 'changed the SOP',
  'action_url.updated': 'changed an action URL',
  'action_url.secret_rolled': 'rolled an action signing secret',
  'action.registered': 'connected an action',
  'action.unregistered': 'disconnected an action',
}

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/** Timezones offered in settings: India first, then the zones our customers' teams most often work in. */
export const TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
  'UTC',
]

/** Waitlist answers (apps/web worker/common/enums.ts) in words. */
export const USE_CASE_LABEL: Record<string, string> = {
  checkout_recovery: 'Checkout recovery',
  checkout_help: 'Checkout help',
  order_verification: 'Order verification',
  delivery_slot: 'Delivery slots',
  failed_delivery: 'Failed deliveries',
  return_save: 'Return save',
  other: 'Other',
}

/** How many orders a store takes a month, from the website's Book a call form (apps/web OrdersPerMonth). */
export const ORDERS_PER_MONTH_LABEL: Record<string, string> = {
  under_1000: 'Under 1,000',
  '1000_10000': '1,000–10,000',
  over_10000: 'Over 10,000',
}

export const EMPLOYEES_LABEL: Record<string, string> = {
  under_50: 'Under 50',
  '50_199': '50–199',
  '200_999': '200–999',
  '1000_4999': '1,000–4,999',
  '5000_plus': '5,000+',
}
